import { useEffect } from 'react';
import { useCampaignUiStore } from '@discovr/features';

/**
 * Publishes the RESOLVED reporting-window selection (Task 12) into the
 * shared campaign UI store — mirrors usePublishSharePageInfo's rationale
 * exactly: the admin-header page-kind ShareButton has no direct access to
 * either windowed reporting sub-tab's local state, and it needs the
 * RESOLVED value — not the raw `?win` URL param, which is absent whenever
 * the admin hasn't touched the dropdown yet (the common case for a
 * freshly-opened tab, where the on-screen default is DEFAULT_TIME_PERIOD)
 * — so a share minted from that moment actually
 * matches what's rendered on screen instead of silently sending `window: ''`.
 *
 * `window: null` is a deliberate no-op — it means "I don't own this slot
 * right now" (e.g. CampaignOverviewTab while a non-windowed sub-tab like
 * Creator Content is active) and neither publishes nor clears the store.
 * This matters because BOTH CampaignOverviewTab (parent, always mounted) and
 * SentimentAnalysisTab (child, mounted only while active) call this hook —
 * treating `null` as "publish null" here would let the parent's effect race
 * the child's and clobber the real value on every overview↔sentiment
 * transition (React commits child effects before parent effects). Passing
 * `null` sidesteps that entirely: only the ACTUALLY active windowed tab ever
 * touches the store, and unmounting (or losing "owner" status) clears it via
 * this effect's own cleanup.
 */
export function usePublishShareWindow(window: string | null): void {
  const setShareWindow = useCampaignUiStore((s) => s.setShareWindow);

  useEffect(() => {
    if (window == null) return;
    setShareWindow(window);
    return () => setShareWindow(null);
  }, [window, setShareWindow]);
}
